import { Plus, Trash2 } from "lucide-react";
import PickList from "./PickList";
import { CURRENCIES, emptyRow } from "./reminderMessage";

const input = "w-full h-11 px-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm bg-white";
const lbl = "block text-xs font-semibold text-slate-600";
const opt = <span className="font-normal text-slate-400">(optional)</span>;

export const FIELD_LABELS = {
    amount: "Fee amount", pending: "Pending amount", due_date: "Payment due date", promised_date: "Promised payment date",
    prev_date: "Previous reminder date", next_cycle: "Next cycle start date", status: "Class status", notes: "Additional notes",
};

const groupsFor = (options, key) => [
    { label: "This parent's", items: options?.parent?.[key] || [] },
    { label: "All", items: options?.all?.[key] || [] },
];

function Row({ row, i, options, onChange, onRemove, canRemove, onSaveMaster }) {
    const set = (patch) => onChange({ ...row, ...patch });
    const save = (kind) => (v) => onSaveMaster?.(kind, v);
    return (
        <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-[#F8FAFC]" data-testid={`fee-row-${i}`}>
            <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500">Combination {i + 1}</span>
                {canRemove && (
                    <button type="button" onClick={onRemove} aria-label="Remove combination" data-testid={`fee-row-remove-${i}`}
                        className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                )}
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
                <div className={lbl}>Tutor {opt}<div className="mt-1"><PickList testid={`fee-row-${i}-tutor`} groups={groupsFor(options, "tutors")} value={row.tutor} onChange={(v) => set({ tutor: v })} placeholder="Select tutor" manualLabel="Tutor" onSaveMaster={save("tutors")} /></div></div>
                <label className={lbl}>Number of sessions {opt}
                    <input type="number" min="1" max="999" inputMode="numeric" value={row.sessions} data-testid={`fee-row-${i}-sessions`}
                        onChange={(e) => set({ sessions: e.target.value.replace(/\D/g, "").slice(0, 3) })} className={`${input} mt-1`} placeholder="e.g. 16" />
                </label>
                <div className={lbl}>Grade / Class {opt}<div className="mt-1"><PickList multi testid={`fee-row-${i}-grades`} groups={groupsFor(options, "grades")} value={row.grades} onChange={(v) => set({ grades: v })} placeholder="Select one or more" manualLabel="Grade/Class" onSaveMaster={save("grades")} /></div></div>
                <div className={lbl}>Subject {opt}<div className="mt-1"><PickList multi testid={`fee-row-${i}-subjects`} groups={groupsFor(options, "subjects")} value={row.subjects} onChange={(v) => set({ subjects: v })} placeholder="Select one or more" manualLabel="Subject" onSaveMaster={save("subjects")} /></div></div>
            </div>
        </div>
    );
}

function ExtraField({ k, fields, setField }) {
    if (k === "amount" || k === "pending") {
        const ck = k === "amount" ? "currency" : "pending_currency";
        return (
            <label className={lbl}>{FIELD_LABELS[k]} {opt}
                <div className="mt-1 flex gap-2">
                    <select value={fields[ck] || fields.currency || "INR"} onChange={(e) => setField(ck, e.target.value)} data-testid={`fee-${k}-currency`}
                        className="h-11 px-2 rounded-lg border border-slate-300 text-sm bg-white">{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select>
                    <input inputMode="decimal" value={fields[k] || ""} onChange={(e) => setField(k, e.target.value.replace(/[^\d.,]/g, "").replace(/(\..*)\./g, "$1"))} data-testid={`fee-${k}`} className={input} placeholder="e.g. 5000" />
                </div>
            </label>
        );
    }
    if (k === "status") {
        return (
            <label className={lbl}>{FIELD_LABELS[k]} {opt}
                <select value={fields.status || ""} onChange={(e) => setField("status", e.target.value)} data-testid="fee-status" className={`${input} mt-1`}>
                    <option value="">Not mentioned</option><option value="active">Active</option>
                    <option value="hold">Temporarily on hold</option><option value="awaiting">Awaiting confirmation</option>
                </select>
            </label>
        );
    }
    if (k === "notes") {
        return (
            <label className={`${lbl} sm:col-span-2`}>{FIELD_LABELS[k]} {opt}
                <textarea rows={2} value={fields.notes || ""} onChange={(e) => setField("notes", e.target.value)} data-testid="fee-notes"
                    className="mt-1 w-full p-3 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm" />
            </label>
        );
    }
    return (
        <label className={lbl}>{FIELD_LABELS[k]} {opt}
            <input type="date" value={fields[k] || ""} onChange={(e) => setField(k, e.target.value)} data-testid={`fee-${k.replace(/_/g, "-")}`} className={`${input} mt-1`} />
        </label>
    );
}

/** Optional reminder details: combinations + the extra fields that fit the selected template. */
export default function DetailsPanel({ state, setState, options, visible, showAll, setShowAll, onSaveMaster }) {
    const { rows, fields } = state;
    const setField = (k, v) => setState((s) => ({ ...s, fields: { ...s.fields, [k]: v } }));
    const setRow = (i, r) => setState((s) => ({ ...s, rows: s.rows.map((x, j) => (j === i ? r : x)) }));
    const all = Object.keys(FIELD_LABELS);
    const keys = showAll ? all : all.filter((k) => visible.includes(k) || fields[k]);
    const hidden = all.filter((k) => !keys.includes(k));
    return (
        <div className="space-y-3" data-testid="fee-details-panel">
            {visible.includes("rows") || showAll ? (
                <>
                    {rows.map((r, i) => (
                        <Row key={i} i={i} row={r} options={options} onChange={(x) => setRow(i, x)} canRemove={rows.length > 1} onSaveMaster={onSaveMaster}
                            onRemove={() => setState((s) => ({ ...s, rows: s.rows.filter((_, j) => j !== i) }))} />
                    ))}
                    <button type="button" onClick={() => setState((s) => ({ ...s, rows: [...s.rows, emptyRow()] }))} data-testid="fee-row-add"
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1E3A8A] hover:underline"><Plus className="w-4 h-4" /> Add another combination</button>
                </>
            ) : null}
            <div className="grid sm:grid-cols-2 gap-3">
                {keys.map((k) => <ExtraField key={k} k={k} fields={fields} setField={setField} />)}
            </div>
            {hidden.length > 0 && (
                <button type="button" onClick={() => setShowAll(true)} data-testid="fee-more-fields" className="text-xs font-bold text-[#1E3A8A] hover:underline">
                    More fields ({hidden.map((k) => FIELD_LABELS[k]).join(", ")})
                </button>
            )}
            {showAll && (
                <button type="button" onClick={() => setShowAll(false)} data-testid="fee-fewer-fields" className="text-xs font-bold text-slate-500 hover:underline ml-3">Fewer fields</button>
            )}
        </div>
    );
}
